// Cards the AI assistant sends for chat entries (check-in advance, expenses, ...).
// They travel in the plain-text stream as a trailing marker:
//   [[CARD:<base64url(json)>]]
// The server (api chat/actions/cardProtocol.ts) is the only producer. Keep this
// file identical to app/src/Components/chatCard.ts.

export interface ChatCardChoice {
  label: string;
  value: string;
}

export interface ChatCard {
  v: 1;
  kind: 'confirm' | 'info' | 'choices';
  draftId?: number;
  entry?: string;
  title: string;
  rows?: { label: string; value: string }[];
  warnings?: string[];
  expiresAt?: string;
  options?: ChatCardChoice[];
  deepLink?: { path: string; label: string };
  actions?: { id: 'confirm' | 'cancel'; label: string }[];
}

const COMPLETE_RE = /\[\[CARD:([A-Za-z0-9_-]+)\]\]/;
// Any prefix of the marker at the very end of a still-streaming message (even a lone "[").
const PARTIAL_TAIL_RE = /\[(?:\[(?:C(?:A(?:R(?:D(?::[A-Za-z0-9_-]*\]?)?)?)?)?)?)?$/;

// Self-contained base64url + UTF-8 decoding: React Native's Hermes has no TextDecoder
// (and atob only on recent versions), and the rupee sign needs real UTF-8 handling.
const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

function b64urlToBytes(input: string): number[] {
  const bytes: number[] = [];
  let buffer = 0;
  let bits = 0;
  for (const ch of input) {
    const v = B64.indexOf(ch);
    if (v < 0) continue;
    buffer = (buffer << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((buffer >> bits) & 0xff);
    }
  }
  return bytes;
}

function utf8(bytes: number[]): string {
  let out = '';
  for (let i = 0; i < bytes.length; ) {
    const b = bytes[i++];
    if (b < 0x80) out += String.fromCharCode(b);
    else if (b < 0xe0) out += String.fromCharCode(((b & 0x1f) << 6) | (bytes[i++] & 0x3f));
    else if (b < 0xf0) {
      out += String.fromCharCode(((b & 0x0f) << 12) | ((bytes[i++] & 0x3f) << 6) | (bytes[i++] & 0x3f));
    } else {
      const cp =
        ((b & 0x07) << 18) | ((bytes[i++] & 0x3f) << 12) | ((bytes[i++] & 0x3f) << 6) | (bytes[i++] & 0x3f);
      const c = cp - 0x10000;
      out += String.fromCharCode(0xd800 + (c >> 10), 0xdc00 + (c & 0x3ff));
    }
  }
  return out;
}

function decode(b64url: string): ChatCard | null {
  try {
    const card = JSON.parse(utf8(b64urlToBytes(b64url)));
    return card && card.v === 1 && typeof card.title === 'string' ? (card as ChatCard) : null;
  } catch {
    return null;
  }
}

/**
 * Splits streamed text into display text and an optional card. Safe to call on
 * every chunk: a half-received marker is hidden until it completes.
 */
export function extractCard(text: string): { text: string; card?: ChatCard } {
  const m = COMPLETE_RE.exec(text);
  if (m) {
    const card = decode(m[1]) || undefined;
    return { text: text.replace(COMPLETE_RE, '').trim(), card };
  }
  return { text: text.replace(PARTIAL_TAIL_RE, '').trim() };
}
